import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { defRoute, LoadedProps, RouteLoadProps } from '@components/router';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabWrapper } from '@course/components/admin-nav';
import { IcoArrowLeft, IcoExternalLink, IcoInfo } from '@components/icons';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Spinner } from '@components/spinner';
import { showError } from '@components/app-error';

type Funnel = RpxResponse<typeof rpx.campaignFunnel.getCampaignFunnel>;
type Summary = RpxResponse<typeof rpx.campaignFunnel.getCampaignFunnelSummary>;
type Timeseries = RpxResponse<typeof rpx.campaignFunnel.getCampaignFunnelTimeseries>;
type UpsellImpact = RpxResponse<typeof rpx.campaignFunnel.getCampaignUpsellImpact>;
type Campaign = RpxResponse<typeof rpx.campaigns.getCampaign>;
type CampaignPrice = Campaign['prices'][0];
type StepKey =
  | 'visit'
  | 'checkout_started'
  | 'payment_reached'
  | 'purchased'
  | 'upsell_shown'
  | 'upsell_accepted';

const STEP_LABELS: Record<StepKey, string> = {
  visit: 'Visited',
  checkout_started: 'Started checkout',
  payment_reached: 'Reached payment',
  purchased: 'Purchased',
  upsell_shown: 'Saw upsell',
  upsell_accepted: 'Accepted upsell',
};

const PRIMARY_STEPS: StepKey[] = ['visit', 'checkout_started', 'payment_reached', 'purchased'];

const POOR_RETENTION_THRESHOLD = 0.2;
const WEEKLY_GRANULARITY_DAYS = 90;

type Range = {
  startDate: string;
  endDate: string;
  label: string;
  key: string;
};

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIsoDate(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function tomorrowIsoDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function buildPresets(firstVisitDate: string | undefined): Range[] {
  const presets: Range[] = [];
  if (firstVisitDate) {
    presets.push({
      key: 'launch',
      label: 'This launch',
      startDate: firstVisitDate,
      endDate: tomorrowIsoDate(),
    });
  }
  presets.push(
    { key: '7d', label: 'Last 7 days', startDate: daysAgoIsoDate(7), endDate: tomorrowIsoDate() },
    {
      key: '30d',
      label: 'Last 30 days',
      startDate: daysAgoIsoDate(30),
      endDate: tomorrowIsoDate(),
    },
    {
      key: '90d',
      label: 'Last 90 days',
      startDate: daysAgoIsoDate(90),
      endDate: tomorrowIsoDate(),
    },
    { key: 'all', label: 'All time', startDate: '2000-01-01', endDate: tomorrowIsoDate() },
  );
  return presets;
}

function addOneDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
}

function buildCustomRange(startDate: string, endDate: string): Range {
  return {
    key: 'custom',
    label: 'Custom',
    startDate,
    endDate: addOneDay(endDate),
  };
}

function rangeSpanDays(range: Range): number {
  const start = new Date(`${range.startDate}T00:00:00Z`);
  const end = new Date(`${range.endDate}T00:00:00Z`);
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 86_400_000));
}

function granularityForRange(range: Range): 'day' | 'week' {
  return rangeSpanDays(range) > WEEKLY_GRANULARITY_DAYS ? 'week' : 'day';
}

function formatRelativeStart(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

async function load(route: RouteLoadProps) {
  const courseId = route.params.courseId;
  const [campaign, launchStart] = await Promise.all([
    rpx.campaigns.getCampaign({ courseId }),
    rpx.campaignFunnel.getCampaignLaunchStart({ campaignId: courseId }),
  ]);
  return { courseId, campaign, launchStart };
}

export const route = defRoute({
  load,
  Page,
  authLevel: 'guide',
  hideChat: true,
});

type Props = LoadedProps<typeof load>;

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function formatDollarsFromCents(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) {
    return `$${dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  if (Number.isInteger(dollars)) {
    return `$${dollars}`;
  }
  return `$${dollars.toFixed(2)}`;
}

function formatPriceLabel(price: CampaignPrice): string {
  const amount = formatDollarsFromCents(price.priceInCents);
  if (price.paymentType === 'subscription') {
    const intervalShort =
      price.interval === 'month'
        ? 'mo'
        : price.interval === 'year'
          ? 'yr'
          : price.interval || 'period';
    return `${amount}/${intervalShort}`;
  }
  if (price.paymentType === 'paymentplan') {
    if (price.numPayments && price.numPayments > 1) {
      return `${amount} × ${price.numPayments} PP`;
    }
    return `${amount} PP`;
  }
  return amount;
}

function summarizeUpsell(upsell: NonNullable<Campaign['upsell']>): string {
  return `+ ${upsell.upsellCourseTitle}`;
}

function CampaignContextHeader({ campaign, courseId }: { campaign: Campaign; courseId: string }) {
  const enabledPrices = campaign.prices.filter((p) => p.isEnabled);
  const priceLabels = enabledPrices.map(formatPriceLabel);
  const slug = campaign.course.campaignSlug;
  return (
    <div class="flex flex-col gap-2 mb-6">
      <div class="flex items-center gap-3 text-sm text-gray-500">
        <a
          href={`/admin/campaigns/${courseId}`}
          class="flex items-center gap-1 hover:text-gray-700"
        >
          <IcoArrowLeft class="size-4" />
          All campaigns
        </a>
        {slug && (
          <>
            <span class="text-gray-300">·</span>
            <a
              href={`/c/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 font-mono hover:text-gray-700"
            >
              /c/{slug}
              <IcoExternalLink class="size-3" />
            </a>
          </>
        )}
      </div>
      <h1 class="text-xl font-semibold text-gray-900">{campaign.course.title}</h1>
      <div class="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm text-gray-600">
        {priceLabels.length > 0 && <span class="tabular-nums">{priceLabels.join(' · ')}</span>}
        {campaign.upsell && <span class="text-gray-500">{summarizeUpsell(campaign.upsell)}</span>}
      </div>
    </div>
  );
}

function LaunchDatePopover({
  campaignId,
  manualLaunchDate,
  firstVisitDate,
  onSaved,
}: {
  campaignId: string;
  manualLaunchDate: string | undefined;
  firstVisitDate: string | undefined;
  onSaved: (date: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string>(manualLaunchDate ?? firstVisitDate ?? todayIsoDate());
  const [saving, setSaving] = useState(false);

  async function save(date: string | undefined) {
    setSaving(true);
    try {
      await rpx.campaignFunnel.setCampaignLaunchDate({
        campaignId,
        launchDate: date,
      });
      onSaved(date);
      setOpen(false);
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <span class="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        class="text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline"
      >
        edit
      </button>
      {open && (
        <div class="absolute top-full left-0 mt-1 z-10 w-72 bg-white rounded-lg border border-gray-200 shadow-lg p-3 flex flex-col gap-2">
          <div class="text-[12px] text-gray-700">Launch date</div>
          <input
            type="date"
            value={draft}
            onChange={(e) => setDraft((e.target as HTMLInputElement).value)}
            class="text-sm border border-gray-300 rounded px-2 py-1"
          />
          <div class="text-[11px] text-gray-500">
            Drives the “This launch” period. Leaves other presets unchanged.
            {firstVisitDate && <> First tracked visit: {formatRelativeStart(firstVisitDate)}.</>}
          </div>
          <div class="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              disabled={saving || !manualLaunchDate}
              onClick={() => save(undefined)}
              class="text-[12px] text-gray-500 hover:text-gray-700 disabled:opacity-40"
            >
              Reset to first visit
            </button>
            <div class="flex items-center gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setOpen(false)}
                class="text-[12px] text-gray-600 hover:text-gray-900 px-2 py-1"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving || !draft}
                onClick={() => save(draft)}
                class="text-[12px] text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded px-3 py-1"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div class="flex flex-col gap-1 p-4 bg-white rounded-xl border border-gray-200">
      <div class="text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
      <div class="text-[12px] text-gray-500 uppercase tracking-wide">{label}</div>
      {hint && <div class="text-[11px] text-gray-400">{hint}</div>}
    </div>
  );
}

function PurchasesTile({ summary }: { summary: Summary }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const paid = summary.paidCount;
  const planActive = summary.paymentPlanActiveCount;
  const planCanceled = summary.paymentPlanCanceledCount;
  const planTotal = planActive + planCanceled;
  const planCancelRate = planTotal > 0 ? planCanceled / planTotal : 0;
  const hasBreakdown = summary.purchaseCount > 0;

  function openNow() {
    if (closeTimer.current !== undefined) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
    setOpen(true);
  }

  function closeSoon() {
    if (closeTimer.current !== undefined) {
      window.clearTimeout(closeTimer.current);
    }
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      closeTimer.current = undefined;
    }, 150);
  }

  return (
    <div
      class={`relative flex flex-col gap-1 p-4 bg-white rounded-xl border border-gray-200 transition-colors ${
        hasBreakdown ? 'cursor-help hover:border-gray-300 hover:bg-gray-50/60' : ''
      }`}
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <div class="text-2xl font-semibold text-gray-900 tabular-nums">
        {summary.purchaseCount.toLocaleString()}
      </div>
      <div class="flex items-center gap-1.5 text-[12px] text-gray-500 uppercase tracking-wide">
        <span>Purchases</span>
        {hasBreakdown && (
          <button
            type="button"
            aria-label="Show purchases breakdown"
            aria-expanded={open}
            onClick={openNow}
            onFocus={openNow}
            onBlur={closeSoon}
            class="text-indigo-500 hover:text-indigo-700 focus:text-indigo-700 focus:outline-none cursor-help"
          >
            <IcoInfo class="size-4" />
          </button>
        )}
      </div>
      {hasBreakdown && open && (
        <div
          role="tooltip"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          class="absolute top-full left-0 z-20 w-64 bg-white rounded-lg border border-gray-200 shadow-lg p-3 flex flex-col gap-2 text-left normal-case tracking-normal"
        >
          <div class="flex items-center justify-between text-[12px]">
            <span class="text-gray-700">Full pay</span>
            <span class="text-gray-900 font-medium tabular-nums">{paid.toLocaleString()}</span>
          </div>
          <div class="flex flex-col gap-1 text-[12px]">
            <div class="flex items-center justify-between">
              <span class="text-gray-700">Payment plans</span>
              <span class="text-gray-900 font-medium tabular-nums">
                {planTotal.toLocaleString()}
              </span>
            </div>
            {planTotal > 0 && (
              <div class="flex flex-col gap-0.5 pl-3 text-[11px] text-gray-500">
                <div class="flex items-center justify-between">
                  <span>Active</span>
                  <span class="tabular-nums">{planActive.toLocaleString()}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span>Canceled</span>
                  <span class="tabular-nums">{planCanceled.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
          {planTotal > 0 && (
            <div class="border-t border-gray-100 pt-2 flex items-center justify-between text-[11px] text-gray-500">
              <span>Plan cancel rate</span>
              <span class="tabular-nums">{formatPercent(planCancelRate)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FunnelStatTiles({ summary }: { summary: Summary }) {
  const visitToBuy = summary.visitors > 0 ? formatPercent(summary.visitToBuyRate) : '—';
  const revenue = summary.purchaseCount > 0 ? formatDollarsFromCents(summary.revenueCents) : '—';
  const avgOrder = summary.purchaseCount > 0 ? formatDollarsFromCents(summary.avgOrderCents) : '—';
  const deferred = summary.deferredCents;
  const months = summary.maxMonthsRemaining;
  const deferredHint =
    deferred > 0 && months > 0
      ? `incl. ${formatDollarsFromCents(deferred)} due over next ${months === 1 ? 'month' : `${months} months`}`
      : undefined;
  const pwycHint = summary.pwycCount > 0 ? 'actual pay-what-you-can amounts' : undefined;
  const revenueHint = deferredHint ?? pwycHint;
  return (
    <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      <StatTile label="Visitors" value={summary.visitors.toLocaleString()} />
      <PurchasesTile summary={summary} />
      <StatTile label="Visit → Buy" value={visitToBuy} />
      <StatTile label="Revenue" value={revenue} hint={revenueHint} />
      <StatTile label="Avg order" value={avgOrder} />
    </div>
  );
}

function CohortBar({
  label,
  count,
  widthPct,
  isAccent,
  isOutcome,
  outcomeNote,
}: {
  label: string;
  count: number;
  widthPct: number;
  isAccent?: boolean;
  isOutcome?: boolean;
  outcomeNote?: string;
}) {
  const safeWidth = Math.min(100, Math.max(2, widthPct));
  if (isOutcome) {
    return (
      <div class="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg bg-emerald-50/60">
        <div class="w-40 flex-shrink-0 text-sm font-semibold text-gray-900">{label}</div>
        <div class="flex-1">
          <div class="h-7 rounded bg-emerald-600" style={`width: ${safeWidth}%`} />
        </div>
        <div class="w-32 text-right tabular-nums">
          <div class="text-base font-semibold text-gray-900">{count.toLocaleString()}</div>
          {outcomeNote && <div class="text-[11px] font-medium text-emerald-700">{outcomeNote}</div>}
        </div>
      </div>
    );
  }
  return (
    <div class="flex items-center gap-3">
      <div class="w-40 flex-shrink-0 text-sm text-gray-700">{label}</div>
      <div class="flex-1">
        <div
          class={`h-6 rounded ${isAccent ? 'bg-indigo-500' : 'bg-slate-700'}`}
          style={`width: ${safeWidth}%`}
        />
      </div>
      <div class="w-32 text-right text-sm font-medium text-gray-900 tabular-nums">
        {count.toLocaleString()}
      </div>
    </div>
  );
}

function DropConnector({ rate }: { rate: number }) {
  const isPoor = rate > 0 && rate < POOR_RETENTION_THRESHOLD;
  return (
    <div class="flex items-center gap-3 my-1">
      <div class="w-40 flex-shrink-0" />
      <div class="flex-1">
        <span
          class={`text-[11px] tabular-nums ${
            isPoor ? 'text-amber-700 font-semibold' : 'text-gray-400'
          }`}
        >
          → {formatPercent(rate)} continued
        </span>
      </div>
      <div class="w-32" />
    </div>
  );
}

function CohortFunnel({ funnel, hasUpsell }: { funnel: Funnel; hasUpsell: boolean }) {
  const stepMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of funnel.steps) {
      m[s.eventType] = s.count;
    }
    return m;
  }, [funnel]);

  const visit = stepMap.visit ?? 0;
  const purchased = stepMap.purchased ?? 0;
  const upsellShown = stepMap.upsell_shown ?? 0;
  const upsellAccepted = stepMap.upsell_accepted ?? 0;
  const showUpsellRows = hasUpsell && purchased > 0;

  if (visit === 0) {
    return (
      <div class="text-sm text-gray-500 italic py-6 text-center">No traffic in this period.</div>
    );
  }

  return (
    <div class="flex flex-col">
      {PRIMARY_STEPS.map((eventType, i) => {
        const count = stepMap[eventType] ?? 0;
        const widthPct = visit > 0 ? (count / visit) * 100 : 0;
        const prev = i > 0 ? (stepMap[PRIMARY_STEPS[i - 1]] ?? 0) : count;
        const rate = prev > 0 ? count / prev : 0;
        const isOutcome = eventType === 'purchased';
        const outcomeNote =
          isOutcome && visit > 0 ? `${((count / visit) * 100).toFixed(1)}% of visits` : undefined;
        return (
          <div key={eventType} class="flex flex-col">
            {i > 0 && <DropConnector rate={rate} />}
            <CohortBar
              label={STEP_LABELS[eventType]}
              count={count}
              widthPct={widthPct}
              isOutcome={isOutcome}
              outcomeNote={outcomeNote}
            />
          </div>
        );
      })}
      {showUpsellRows && (
        <div class="flex flex-col mt-4 pt-4 border-t border-gray-100">
          <div class="flex items-center gap-3 mb-2">
            <div class="w-40 flex-shrink-0 text-[11px] uppercase tracking-wide text-gray-400">
              Of buyers
            </div>
            <div class="flex-1" />
            <div class="w-32" />
          </div>
          <CohortBar
            label={STEP_LABELS.upsell_shown}
            count={upsellShown}
            widthPct={purchased > 0 ? (upsellShown / purchased) * 100 : 0}
          />
          <DropConnector rate={upsellShown > 0 ? upsellAccepted / upsellShown : 0} />
          <CohortBar
            label={STEP_LABELS.upsell_accepted}
            count={upsellAccepted}
            widthPct={purchased > 0 ? (upsellAccepted / purchased) * 100 : 0}
            isAccent
          />
        </div>
      )}
    </div>
  );
}

type DailyPoint = { date: string; visits: number; purchases: number };

function pivotTimeseries(timeseries: Timeseries, granularity: 'day' | 'week'): DailyPoint[] {
  const map: Record<string, DailyPoint> = {};
  for (const r of timeseries.rows) {
    if (!map[r.bucket]) {
      map[r.bucket] = { date: r.bucket, visits: 0, purchases: 0 };
    }
    if (r.eventType === 'visit') {
      map[r.bucket].visits = r.sessions;
    }
    if (r.eventType === 'purchased') {
      map[r.bucket].purchases = r.sessions;
    }
  }
  const dates = Object.keys(map).sort();
  if (dates.length === 0) {
    return [];
  }
  const stepDays = granularity === 'week' ? 7 : 1;
  const first = new Date(`${dates[0]}T00:00:00Z`);
  const last = new Date(`${dates[dates.length - 1]}T00:00:00Z`);
  const out: DailyPoint[] = [];
  for (let d = new Date(first); d <= last; d.setUTCDate(d.getUTCDate() + stepDays)) {
    const key = d.toISOString().slice(0, 10);
    out.push(map[key] ?? { date: key, visits: 0, purchases: 0 });
  }
  return out;
}

function formatBucketLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatUpsellPriceLabel(u: NonNullable<UpsellImpact['upsell']>): string {
  const amount = formatDollarsFromCents(u.priceInCents);
  if (u.paymentType === 'subscription') {
    const intervalShort =
      u.interval === 'month' ? 'mo' : u.interval === 'year' ? 'yr' : u.interval || 'period';
    return `${amount}/${intervalShort} subscription`;
  }
  if (u.paymentType === 'paymentplan' && u.numPayments && u.numPayments > 1) {
    return `${amount} × ${u.numPayments} payment plan`;
  }
  return `${amount} one-time`;
}

function UpsellImpactSection({
  impact,
  buyerCount,
  sawUpsellCount,
}: {
  impact: UpsellImpact;
  buyerCount: number;
  sawUpsellCount: number;
}) {
  if (!impact.upsell) {
    return null;
  }
  const u = impact.upsell;
  const isSub = u.paymentType === 'subscription';
  const acceptRate = buyerCount > 0 ? u.acceptedCount / buyerCount : 0;
  const sawRate = buyerCount > 0 ? sawUpsellCount / buyerCount : 0;
  const skippedCount = Math.max(0, sawUpsellCount - u.acceptedCount);
  const cohortParts: string[] = [];
  if (buyerCount > 0) {
    cohortParts.push(
      `${u.acceptedCount} of ${buyerCount} buyer${buyerCount === 1 ? '' : 's'} accepted`,
    );
  }
  if (sawUpsellCount > 0 && sawRate < 1) {
    cohortParts.push(`${(sawRate * 100).toFixed(0)}% saw the offer`);
  } else if (sawUpsellCount > 0) {
    cohortParts.push('100% saw the offer');
  }
  if (skippedCount > 0) {
    cohortParts.push(
      `${skippedCount} skipped${u.canceledCount > 0 ? `, ${u.canceledCount} canceled` : ''}`,
    );
  } else if (u.canceledCount > 0) {
    cohortParts.push(`${u.canceledCount} canceled`);
  }
  if (isSub && u.freeTrialPeriod > 0) {
    cohortParts.push(`${u.freeTrialPeriod}-day free trial`);
  }
  const monthLabel = isSub && u.interval === 'year' ? 'Booked Year 1' : 'Booked period 1';
  return (
    <section class="bg-white rounded-xl border border-gray-200 p-6">
      <div class="flex flex-col gap-1 mb-4">
        <h2 class="text-sm font-semibold text-gray-900">Upsell impact</h2>
        <div class="text-[12px] text-gray-500">
          {u.courseTitle} · {formatUpsellPriceLabel(u)}
        </div>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <StatTile
          label="Accept rate"
          value={buyerCount > 0 ? `${(acceptRate * 100).toFixed(0)}%` : '—'}
        />
        <StatTile label="Accepted" value={u.acceptedCount.toLocaleString()} />
        <StatTile
          label={isSub ? 'Booked Month 1' : monthLabel}
          value={formatDollarsFromCents(u.monthOneBookedCents)}
        />
        <StatTile
          label="Year 1 booked"
          value={formatDollarsFromCents(u.yearOneBookedCents)}
          hint={isSub ? 'no churn' : undefined}
        />
      </div>
      {cohortParts.length > 0 && (
        <div class="text-[12px] text-gray-500">{cohortParts.join(' · ')}</div>
      )}
    </section>
  );
}

function DailyTimeseriesChart({ points }: { points: DailyPoint[] }) {
  const [hoverIdx, setHoverIdx] = useState<number | undefined>(undefined);
  if (points.length === 0) {
    return (
      <div class="text-sm text-gray-500 italic py-6 text-center">No activity in this period.</div>
    );
  }
  const maxVal = Math.max(1, ...points.map((p) => Math.max(p.visits, p.purchases)));
  const chartHeight = 160;
  const labelStride = Math.max(1, Math.ceil(points.length / 8));
  const hoverPoint = hoverIdx !== undefined ? points[hoverIdx] : undefined;
  return (
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-4 text-[12px] text-gray-600">
        <div class="flex items-center gap-1.5">
          <span class="size-2.5 rounded-sm bg-slate-300" />
          Visits
        </div>
        <div class="flex items-center gap-1.5">
          <span class="size-2.5 rounded-sm bg-indigo-500" />
          Purchases
        </div>
        {hoverPoint ? (
          <div class="ml-auto text-gray-700 tabular-nums">
            {formatBucketLabel(hoverPoint.date)} · {hoverPoint.visits.toLocaleString()} visits ·{' '}
            {hoverPoint.purchases.toLocaleString()} purchases
            {hoverPoint.visits > 0 && (
              <> · {formatPercent(hoverPoint.purchases / hoverPoint.visits)} conv</>
            )}
          </div>
        ) : (
          <div class="ml-auto text-gray-400 tabular-nums">
            peak {maxVal.toLocaleString()}/day
          </div>
        )}
      </div>
      <div class="relative" style={`height: ${chartHeight + 28}px`}>
        <div
          class="absolute inset-x-0 border-t border-dashed border-gray-200"
          style="top: 0px"
        />
        <div class="absolute inset-x-0 border-t border-gray-200" style={`top: ${chartHeight}px`} />
        <div
          class="flex items-end gap-0.5 absolute inset-x-0 top-0"
          style={`height: ${chartHeight}px`}
          onMouseLeave={() => setHoverIdx(undefined)}
        >
          {points.map((p, i) => {
            const visitsHeight = (p.visits / maxVal) * chartHeight;
            const purchasesHeight =
              p.purchases > 0 ? Math.max(3, (p.purchases / maxVal) * chartHeight) : 0;
            const isHover = hoverIdx === i;
            return (
              <div
                key={p.date}
                class="flex-1 relative cursor-default"
                style={`height: ${chartHeight}px`}
                onMouseEnter={() => setHoverIdx(i)}
              >
                <div
                  class={`absolute bottom-0 left-0 right-0 rounded-t-[3px] transition-colors ${
                    isHover ? 'bg-slate-300' : 'bg-slate-200'
                  }`}
                  style={`height: ${visitsHeight}px`}
                />
                <div
                  class={`absolute bottom-0 left-0 right-0 rounded-t-[3px] transition-colors ${
                    isHover ? 'bg-indigo-600' : 'bg-indigo-500'
                  }`}
                  style={`height: ${purchasesHeight}px`}
                />
              </div>
            );
          })}
        </div>
        <div class="absolute inset-x-0 bottom-0 flex justify-between text-[10px] text-gray-400 tabular-nums">
          {points.map((p, i) =>
            i % labelStride === 0 || i === points.length - 1 ? (
              <span key={p.date} class="px-px">
                {formatBucketLabel(p.date)}
              </span>
            ) : (
              <span key={p.date} class="px-px" />
            ),
          )}
        </div>
      </div>
    </div>
  );
}

function exportCsv(opts: {
  campaignTitle: string;
  funnel: Funnel;
  summary: Summary;
  range: Range;
}) {
  const rows: string[] = [];
  rows.push(`Campaign,${csvEscape(opts.campaignTitle)}`);
  rows.push(
    `Date range,${
      opts.range.key === 'all' ? 'All time' : `${opts.range.startDate} to ${opts.range.endDate}`
    }`,
  );
  rows.push(`Generated,${new Date().toISOString()}`);
  rows.push('');
  rows.push('Metric,Value');
  rows.push(`Visitors,${opts.summary.visitors}`);
  rows.push(`Purchases,${opts.summary.purchaseCount}`);
  rows.push(`Visit to buy rate,${formatPercent(opts.summary.visitToBuyRate)}`);
  rows.push(`Revenue (cents),${opts.summary.revenueCents}`);
  rows.push(`Average order (cents),${opts.summary.avgOrderCents}`);
  rows.push('');
  rows.push('Step,Sessions,Conversion from previous,Conversion from top');
  for (const step of opts.funnel.steps) {
    const label = (STEP_LABELS as Record<string, string>)[step.eventType] || step.eventType;
    rows.push(
      [
        label,
        step.count,
        formatPercent(step.conversionFromPrev),
        formatPercent(step.conversionFromTop),
      ].join(','),
    );
  }
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `funnel-${opts.range.key}-${todayIsoDate()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function exportBuyersCsv(opts: {
  campaignId: string;
  campaignTitle: string;
  range: Range;
}) {
  const { buyers } = await rpx.campaignFunnel.getCampaignBuyers({
    campaignId: opts.campaignId,
    startDate: opts.range.startDate,
    endDate: opts.range.endDate,
  });
  const rows: string[] = [];
  rows.push(`Campaign,${csvEscape(opts.campaignTitle)}`);
  rows.push(
    `Date range,${
      opts.range.key === 'all' ? 'All time' : `${opts.range.startDate} to ${opts.range.endDate}`
    }`,
  );
  rows.push(`Generated,${new Date().toISOString()}`);
  rows.push('');
  rows.push('Name,Email,Amount,Pay-what-you-can,Payment type,Purchase date,Status');
  for (const b of buyers) {
    rows.push(
      [
        csvEscape(b.name),
        csvEscape(b.email),
        csvEscape(formatDollarsFromCents(b.amountInCents)),
        b.isPwyc ? 'Yes' : 'No',
        csvEscape(b.paymentType),
        b.purchaseDate,
        csvEscape(b.status),
      ].join(','),
    );
  }
  const blob = new Blob(['﻿', rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `buyers-${opts.range.key}-${todayIsoDate()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvEscape(value: string | number): string {
  let s = String(value);
  if (/^[=+\-@\t\r]/.test(s)) {
    s = `'${s}`;
  }
  if (/[,"\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function Page(props: Props) {
  const { courseId, campaign, launchStart } = props.state;
  const [manualLaunchDate, setManualLaunchDate] = useState<string | undefined>(
    launchStart.manualLaunchDate,
  );
  const effectiveLaunchDate = manualLaunchDate ?? launchStart.firstVisitDate;
  const presets = useMemo(() => buildPresets(effectiveLaunchDate), [effectiveLaunchDate]);
  const defaultKey = effectiveLaunchDate ? 'launch' : '30d';
  const [rangeKey, setRangeKey] = useState<string>(defaultKey);
  const [customStart, setCustomStart] = useState<string>(daysAgoIsoDate(7));
  const [customEnd, setCustomEnd] = useState<string>(todayIsoDate());
  const customInvalid = rangeKey === 'custom' && customStart > customEnd;
  const range = useMemo(() => {
    if (rangeKey === 'custom') {
      return buildCustomRange(customStart, customEnd);
    }
    return presets.find((p) => p.key === rangeKey) || presets[0];
  }, [presets, rangeKey, customStart, customEnd]);
  const [funnel, setFunnel] = useState<Funnel>();
  const [summary, setSummary] = useState<Summary>();
  const [timeseries, setTimeseries] = useState<Timeseries>();
  const [upsellImpact, setUpsellImpact] = useState<UpsellImpact>();
  const [loading, setLoading] = useState(false);
  const [exportingBuyers, setExportingBuyers] = useState(false);

  const granularity = useMemo(() => granularityForRange(range), [range]);

  useEffect(() => {
    if (customInvalid) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      rpx.campaignFunnel.getCampaignFunnel({
        campaignId: courseId,
        startDate: range.startDate,
        endDate: range.endDate,
      }),
      rpx.campaignFunnel.getCampaignFunnelSummary({
        campaignId: courseId,
        startDate: range.startDate,
        endDate: range.endDate,
      }),
      rpx.campaignFunnel.getCampaignFunnelTimeseries({
        campaignId: courseId,
        startDate: range.startDate,
        endDate: range.endDate,
        granularity,
      }),
      rpx.campaignFunnel.getCampaignUpsellImpact({
        campaignId: courseId,
        startDate: range.startDate,
        endDate: range.endDate,
      }),
    ])
      .then(([f, s, t, u]) => {
        if (cancelled) {
          return;
        }
        setFunnel(f);
        setSummary(s);
        setTimeseries(t);
        setUpsellImpact(u);
      })
      .catch((err) => {
        if (!cancelled) {
          showError(err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, range.startDate, range.endDate, customInvalid]);

  const points = useMemo(
    () => (timeseries ? pivotTimeseries(timeseries, granularity) : []),
    [timeseries, granularity],
  );

  return (
    <FixedPage title={`Funnel — ${campaign.course.title}`}>
      <FixedContent class="bg-white">
        <AdminNav currentPage="campaigns" pageTitle="Funnel" />
        <AdminTabWrapper>
          <CampaignContextHeader campaign={campaign} courseId={courseId} />

          <div class="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div class="flex items-center gap-2 flex-wrap">
              <div class="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                {presets.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setRangeKey(p.key)}
                    class={`text-sm px-3 py-1.5 rounded ${
                      p.key === rangeKey
                        ? 'bg-white shadow-sm text-gray-900 font-medium'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setRangeKey('custom')}
                  class={`text-sm px-3 py-1.5 rounded ${
                    rangeKey === 'custom'
                      ? 'bg-white shadow-sm text-gray-900 font-medium'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Custom
                </button>
              </div>
              {rangeKey === 'custom' && (
                <div class="flex items-center gap-1.5 text-sm text-gray-700">
                  <input
                    type="date"
                    value={customStart}
                    max={customEnd}
                    onChange={(e) => setCustomStart((e.target as HTMLInputElement).value)}
                    class="border border-gray-300 rounded px-2 py-1"
                  />
                  <span class="text-gray-400">to</span>
                  <input
                    type="date"
                    value={customEnd}
                    min={customStart}
                    max={todayIsoDate()}
                    onChange={(e) => setCustomEnd((e.target as HTMLInputElement).value)}
                    class="border border-gray-300 rounded px-2 py-1"
                  />
                </div>
              )}
            </div>
            <button
              type="button"
              disabled={!funnel || !summary}
              onClick={() => {
                if (funnel && summary) {
                  exportCsv({
                    campaignTitle: campaign.course.title,
                    funnel,
                    summary,
                    range,
                  });
                }
              }}
              class="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
            >
              Export funnel
            </button>
            <button
              type="button"
              disabled={!summary || exportingBuyers}
              onClick={() => {
                setExportingBuyers(true);
                exportBuyersCsv({
                  campaignId: courseId,
                  campaignTitle: campaign.course.title,
                  range,
                })
                  .catch(showError)
                  .finally(() => setExportingBuyers(false));
              }}
              class="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
            >
              {exportingBuyers ? 'Exporting…' : 'Export buyers'}
            </button>
          </div>
          <div class="text-[12px] text-gray-500 mb-6 flex items-center gap-2">
            {rangeKey === 'launch' && effectiveLaunchDate ? (
              <>
                <span>
                  Since {formatRelativeStart(effectiveLaunchDate)}
                  {manualLaunchDate ? ' (manual)' : ''} · all times UTC
                </span>
                <LaunchDatePopover
                  campaignId={courseId}
                  manualLaunchDate={manualLaunchDate}
                  firstVisitDate={launchStart.firstVisitDate}
                  onSaved={(d) => setManualLaunchDate(d)}
                />
              </>
            ) : rangeKey === 'custom' ? (
              <span>
                {customInvalid
                  ? 'End date must be on or after start date.'
                  : `${formatRelativeStart(customStart)} – ${formatRelativeStart(customEnd)} · all times UTC · end date inclusive`}
              </span>
            ) : (
              <span>All times UTC</span>
            )}
          </div>

          {loading && !funnel ? (
            <div class="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : !summary || !funnel ? (
            <div class="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <div class="text-sm font-medium text-gray-700 mb-1">No data yet</div>
              <div class="text-sm text-gray-500">
                The funnel populates once the campaign page receives visitors.
              </div>
            </div>
          ) : (
            <div class="flex flex-col gap-6">
              <FunnelStatTiles summary={summary} />

              <section class="bg-white rounded-xl border border-gray-200 p-6">
                <h2 class="text-sm font-semibold text-gray-900 mb-4">Conversion funnel</h2>
                <CohortFunnel funnel={funnel} hasUpsell={!!campaign.upsell} />
              </section>

              {upsellImpact?.upsell && (
                <UpsellImpactSection
                  impact={upsellImpact}
                  buyerCount={funnel.steps.find((s) => s.eventType === 'purchased')?.count ?? 0}
                  sawUpsellCount={
                    funnel.steps.find((s) => s.eventType === 'upsell_shown')?.count ?? 0
                  }
                />
              )}

              <section class="bg-white rounded-xl border border-gray-200 p-6">
                <h2 class="text-sm font-semibold text-gray-900 mb-4">
                  {granularity === 'week' ? 'Weekly activity' : 'Daily activity'}
                </h2>
                <DailyTimeseriesChart points={points} />
              </section>
            </div>
          )}
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}
